import React, { useState, useEffect } from 'react';
import Modal, { PrimaryButton, SecondaryButton } from './Modal.jsx';
import { classifyLink } from '../../lib/classifyLink.js';

const inputStyle = {
  width: '100%', fontSize: 13, color: '#1A1A1A',
  padding: '7px 9px', border: '1px solid #E0DFD9', borderRadius: 6, background: '#fff', outline: 'none'
};

const CHIP_STYLE = {
  repository:  { label: 'Repository',    background: '#E7F3EC', color: '#1F7A3D' },
  doc:         { label: 'Documentation', background: '#E7EEF7', color: '#1F5A9B' }
};

const emptyRow = () => ({ title: '', url: '', override: null });

// The resolved kind drives the chip and the saved value: an explicit override,
// otherwise the URL-based guess. Null (blank URL) means no chip.
function resolvedKind(row) {
  return row.override ?? classifyLink(row.url);
}

function TypeChip({ kind, onClick }) {
  const cfg = CHIP_STYLE[kind];
  if (!cfg) return null;
  return (
    <button
      type="button"
      onClick={onClick}
      title="Click to switch destination"
      style={{
        flexShrink: 0, padding: '3px 8px', fontSize: 11, fontWeight: 500,
        border: 'none', borderRadius: 999, cursor: 'pointer',
        background: cfg.background, color: cfg.color
      }}
    >{cfg.label}</button>
  );
}

export default function AddLinkModal({ open, onClose, projectId, onSaved }) {
  const [rows, setRows]     = useState([emptyRow()]);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState(null);

  useEffect(() => {
    if (open) {
      setRows([emptyRow()]);
      setError(null);
    }
  }, [open]);

  function updateRow(i, field, value) {
    setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r));
  }

  // Flip the override between the two kinds, seeded from the current resolved kind.
  function toggleKind(i) {
    setRows(prev => prev.map((r, idx) => {
      if (idx !== i) return r;
      const current = resolvedKind(r);
      return { ...r, override: current === 'repository' ? 'doc' : 'repository' };
    }));
  }

  function addRow() {
    setRows(prev => [...prev, emptyRow()]);
  }

  function removeRow(i) {
    setRows(prev => prev.length === 1 ? [emptyRow()] : prev.filter((_, idx) => idx !== i));
  }

  const nonBlank  = rows.filter(r => r.url.trim());
  const canSubmit = nonBlank.length > 0 && !saving;

  async function submit() {
    if (!canSubmit) return;
    setSaving(true);
    setError(null);
    try {
      const links = nonBlank.map(r => ({
        title: r.title.trim(),
        url:   r.url.trim(),
        kind:  resolvedKind(r)
      }));
      const res = await fetch(`/projects/${projectId}/links`, {
        method:      'POST',
        credentials: 'same-origin',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || '',
          'Accept':       'application/json'
        },
        body: JSON.stringify({ links })
      });

      if (res.status === 401) { window.location = '/session/new'; return; }
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error((body.errors && body.errors.join(', ')) || `Save failed (${res.status})`);
      }

      const body = await res.json();
      onSaved({ docs: body.docs || [], repositories: body.repositories || [] });
      onClose();
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Add docs & repositories"
      footer={
        <>
          <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
          <PrimaryButton onClick={submit} disabled={!canSubmit}>
            {saving ? 'Saving…' : (nonBlank.length > 1 ? `Add ${nonBlank.length}` : 'Add')}
          </PrimaryButton>
        </>
      }
    >
      <div className="flex items-center justify-between mb-2 text-[11px]" style={{ color: '#9A9A95', textTransform: 'uppercase', letterSpacing: '0.04em' }}>
        <span style={{ flex: '0 0 34%' }}>Title (optional)</span>
        <span style={{ flex: 1, marginLeft: 8 }}>URL</span>
        <span style={{ width: 24 }} />
      </div>

      <div className="space-y-2">
        {rows.map((r, i) => {
          const kind = resolvedKind(r);
          return (
            <div key={i} className="flex items-center gap-2">
              <input
                style={{ ...inputStyle, flex: '0 0 34%' }}
                value={r.title}
                onChange={(e) => updateRow(i, 'title', e.target.value)}
                placeholder="e.g. API service"
              />
              <input
                style={{ ...inputStyle, flex: 1 }}
                value={r.url}
                onChange={(e) => updateRow(i, 'url', e.target.value)}
                placeholder="https://…"
              />
              <TypeChip kind={kind} onClick={() => toggleKind(i)} />
              <button
                type="button"
                onClick={() => removeRow(i)}
                aria-label="Remove row"
                style={{
                  width: 24, height: 24, padding: 0, border: 'none', background: 'none',
                  color: '#9A9A95', cursor: 'pointer', fontSize: 16, lineHeight: 1
                }}
              >×</button>
            </div>
          );
        })}
      </div>

      <button
        type="button"
        onClick={addRow}
        style={{
          marginTop: 10,
          background: 'none', border: 'none', color: '#1A1A1A',
          fontSize: 12, cursor: 'pointer', padding: '4px 0',
          textDecoration: 'underline'
        }}
      >+ Add another row</button>

      {error && (
        <div style={{ marginTop: 12, color: '#9B1F26', fontSize: 12 }}>
          {error}
        </div>
      )}
    </Modal>
  );
}
